import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "../../api";
import Layout from "../../components/Layout";
import { ArrowLeft, PaperPlaneRight, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtPhone = (n) => (n && n.length === 11 && n.startsWith("1") ? `+1 (${n.slice(1, 4)}) ${n.slice(4, 7)}-${n.slice(7)}` : n || "?");
const fmtTs = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return new Date().toDateString() === dt.toDateString()
    ? dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const CONTACT_LINKS = { owner: "/owners", crew: "/roster", lead: "/leads" };

export default function Messages() {
  const { number } = useParams();
  return number ? <Thread number={number} /> : <ThreadList />;
}

function ThreadList() {
  const [threads, setThreads] = useState(null);
  const load = () => api.get("/sms/threads").then((r) => setThreads(r.data)).catch(() => toast.error("Could not load messages"));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load + polling by design
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <Layout title="Messages">
      {threads?.length === 0 && (
        <div className="card-flat p-6 text-sm text-muted-foreground" data-testid="no-threads">
          No texts yet. Publish your inbound number (Settings → Text alerts) and customer texts will land here.
        </div>
      )}
      <div className="space-y-2" data-testid="thread-list">
        {threads?.map((t) => (
          <Link key={t.number} to={`/messages/${t.number}`} data-testid={`thread-${t.number}`}>
            <div className="card-flat p-4 transition-colors hover:bg-muted/50">
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate font-semibold">
                  {t.contact ? t.contact.name : fmtPhone(t.number)}
                  {t.contact && <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-bold capitalize text-primary">{t.contact.type}</span>}
                  {!t.contact && <span className="ml-2 rounded-full bg-[hsl(43,74%,50%)]/15 px-2 py-0.5 text-xs font-bold text-[hsl(43,64%,35%)]">New lead?</span>}
                  {t.opted_out && <span className="ml-2 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-bold text-destructive" data-testid={`thread-opted-out-${t.number}`}>Opted out</span>}
                </p>
                <span className="shrink-0 text-xs text-muted-foreground">{fmtTs(t.last_at)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-2">
                <p className="truncate text-sm text-muted-foreground">{t.last_direction === "out" ? "You: " : ""}{t.last_body || (t.has_media ? "[photo]" : "")}</p>
                {t.unread > 0 && <span data-testid={`unread-${t.number}`} className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">{t.unread}</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </Layout>
  );
}

function Thread({ number }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [leadName, setLeadName] = useState("");
  const [showConvert, setShowConvert] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get(`/sms/threads/${number}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load thread"));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload per thread + polling by design
  useEffect(() => {
    load();
    api.post(`/sms/threads/${number}/read`).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [number]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/sms/threads/${number}/reply`, { body: reply.trim() });
      setReply("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  const convert = async () => {
    try {
      await api.post(`/sms/threads/${number}/convert-lead`, { name: leadName.trim() });
      toast.success("Saved as a lead — find them under Leads & commissions");
      setShowConvert(false);
      setLeadName("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const c = data?.contact;
  return (
    <Layout title={c ? c.name : fmtPhone(number)}>
      <button data-testid="back-to-threads" onClick={() => nav("/messages")} className="mb-3 flex h-10 items-center gap-1 text-sm font-bold text-primary">
        <ArrowLeft size={16} weight="bold" /> All messages
      </button>
      {c ? (
        <Link to={CONTACT_LINKS[c.type]} data-testid="thread-contact-link" className="card-flat mb-3 block p-3 text-sm font-semibold capitalize text-primary">
          {c.type}: {c.name} →
        </Link>
      ) : (
        <div className="card-flat mb-3 p-3" data-testid="unknown-contact">
          <p className="text-sm font-semibold">Not in your contacts — could be new business.</p>
          {!showConvert ? (
            <button data-testid="convert-lead-btn" onClick={() => setShowConvert(true)} className="mt-2 flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">
              <UserPlus size={16} weight="bold" /> Save as lead
            </button>
          ) : (
            <div className="mt-2 flex gap-2">
              <input data-testid="lead-name-input" className="field-input flex-1" placeholder="Name (optional)" value={leadName} onChange={(e) => setLeadName(e.target.value)} />
              <button data-testid="convert-lead-confirm" onClick={convert} className="h-12 shrink-0 rounded-md bg-primary px-4 text-sm font-bold text-primary-foreground">Save</button>
            </div>
          )}
        </div>
      )}
      {data?.opted_out && (
        <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm font-semibold text-destructive" data-testid="opted-out-banner">
          This number opted out of texts (STOP). Replies are blocked until they text START.
        </div>
      )}
      <div className="space-y-2" data-testid="thread-messages">
        {data?.messages.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2 ${m.direction === "out" ? "bg-primary text-primary-foreground" : "bg-muted"}`} data-testid={`msg-${m.id}`}>
              <p className="text-sm">{m.body || (m.media_urls?.length ? `[${m.media_urls.length} photo${m.media_urls.length > 1 ? "s" : ""} attached]` : "")}</p>
              <p className={`mt-0.5 text-xs ${m.direction === "out" ? "opacity-70" : "text-muted-foreground"}`}>{fmtTs(m.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <input data-testid="reply-input" className="field-input flex-1" placeholder={data?.opted_out ? "Blocked — number opted out" : "Text reply…"}
          value={reply} onChange={(e) => setReply(e.target.value)} disabled={data?.opted_out}
          onKeyDown={(e) => e.key === "Enter" && send()} />
        <button data-testid="reply-send" onClick={send} disabled={busy || data?.opted_out || !reply.trim()}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-40">
          <PaperPlaneRight size={20} weight="bold" />
        </button>
      </div>
    </Layout>
  );
}
